import { themeCssVariables } from '#app/theme/themeCssVariables.js';
import { encodeBase64 } from '@contra/utilities/encodeBase64';
import { css } from 'styled-components';

const getChromeVersion = () => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  const pieces = /Chrom(?:e|ium)\/(\d+)\.(\d+)\.(\d+)\.(\d+)/.exec(
    navigator.userAgent,
  );
  if (pieces === null || pieces.length !== 5) {
    return undefined;
  }

  const matches = pieces.map((piece) => Number.parseInt(piece, 10));

  return {
    build: matches?.[3],
    major: matches?.[1],
    minor: matches?.[2],
    patch: matches?.[4],
  };
};

export const replaceCssColorVariables = (color: string): string => {
  const themeColorIndex = themeCssVariables.findIndex(([key]) => {
    return color === `var(${key})`;
  });

  if (themeColorIndex === -1) {
    return color;
  }

  return themeCssVariables[themeColorIndex]?.[1] ?? color;
};

type DashedBorderBackgroundOptions = {
  radius?: number | string;
  stroke?: string;
  strokeDasharray?: readonly number[];
  strokeDashoffset?: number;
  strokeLinecap?: string;
  /**
   * Should be a number unless a percentage is needed.
   */
  strokeWidth?: number;
};

export const dashedBorderBackground = ({
  radius = 0,
  stroke = '#000',
  strokeDasharray = [1.5, 8],
  strokeDashoffset = 0,
  strokeLinecap = 'butt',
  strokeWidth = 1.5,
}: DashedBorderBackgroundOptions = {}) => {
  const svg = `
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask">
      <rect width="100%" height="100%" rx="${radius}" ry="${radius}" fill="white" />
    </mask>

    <rect
      mask="url(#mask)"
      width="100%"
      height="100%"
      fill="none"
      rx="${radius}"
      ry="${radius}"
      stroke="${replaceCssColorVariables(stroke)}"
      stroke-width="${strokeWidth * 2}"
      stroke-dasharray="${strokeDasharray.join(' ')}"
      stroke-dashoffset="${strokeDashoffset}"
      stroke-linecap="${strokeLinecap}"
    />
  </svg>
  `
    .trim()
    .replaceAll(/\s+/g, ' ');

  const backgroundImage = `url('data:image/svg+xml;base64,${encodeBase64(
    svg,
  )}')`;

  return css`
    background-image: ${backgroundImage};
    border-radius: ${typeof radius === 'number' ? `${radius}px` : radius};
  `;
};

export type GradientBorderBackgroundOptions = {
  colorStart: string;
  colorStop: string;
  radius: number | string;
  rotation?: number;
  stops?: Array<{
    color: string;
    offset: number;
  }>;
  strokeWidth: number;
};

export const gradientBorderBackground = ({
  colorStart,
  colorStop,
  radius,
  rotation = 90,
  stops,
  strokeWidth,
}: GradientBorderBackgroundOptions) => {
  const svg = `
  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <mask id="mask">
        <rect width="100%" height="100%" rx="${radius}" ry="${radius}" fill="white" />
      </mask>

      <linearGradient id="gradientBorder" gradientTransform="rotate(${rotation})">
        ${
          stops
            ? stops
                .map(
                  ({ color, offset }) =>
                    `<stop offset="${offset}%" stop-color="${replaceCssColorVariables(color)}" />`,
                )
                .join('')
            : `
          <stop offset="0" stop-color="${replaceCssColorVariables(
            colorStart,
          )}" />
          <stop offset="100%" stop-color="${replaceCssColorVariables(
            colorStop,
          )}" />
          `
        }
      </linearGradient>
    </defs>

    <rect
      mask="url(#mask)"
      width="100%"
      height="100%"
      fill="none"
      rx="${radius}"
      ry="${radius}"
      stroke="url(#gradientBorder)"
      stroke-width="${strokeWidth}"
    />
  </svg>
  `
    .trim()
    .replaceAll(/\s+/g, ' ');

  const backgroundImage = `url('data:image/svg+xml;base64,${encodeBase64(
    svg,
  )}')`;

  const { major } = getChromeVersion() ?? {};

  const brokenChromeVersion = 108;
  return css`
    background-image: ${major === brokenChromeVersion
      ? 'none'
      : backgroundImage};
    border: ${major === brokenChromeVersion
      ? `2px solid ${colorStart}`
      : 'none'};
    border-radius: ${typeof radius === 'number' ? `${radius}px` : radius};
  `;
};
